import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  effect,
  input,
  OnInit,
  output,
  ViewChild
} from '@angular/core';
import {TimePickerComponent} from '@niceltd/sol/time-picker';
import {TextInputComponent} from '@niceltd/sol/text-input';
import {DateUtils} from '../../../../common/utils/date';
import {Features} from '../../../../models/plan';
import {AuthenticationService} from '../../../../authentication.service';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';

const now = new Date();

@Component({
  standalone: false,
  selector: 'esp-fte-settings',
  templateUrl: './fte-settings.component.html',
  styleUrls: ['./fte-settings.component.css']
})
export class FteSettingsComponent implements OnInit, AfterViewInit {
  @ViewChild('hrsPerDayPicker', { static: false }) hrsPerDayPicker: TimePickerComponent;
  @ViewChild('hrsPerWeekPicker', { static: false }) hrsPerWeekPicker: TimePickerComponent;

  modelChanged = output<{ fteHoursPerDay: any; fteHoursPerWeek: any; fteDaysPerMonth: any; isValid: any }>();
  ctSettings = input.required<{
    fteHoursPerDay:number|string,
    fteHoursPerWeek:number|string,
    fteDaysPerMonth:{
      JANUARY: number|string,
      FEBRUARY: number|string,
      MARCH: number|string,
      APRIL: number|string,
      MAY: number|string,
      JUNE: number|string,
      JULY: number|string,
      AUGUST: number|string,
      SEPTEMBER: number|string,
      OCTOBER: number|string,
      NOVEMBER: number|string,
      DECEMBER: number|string
    }
  }>();

  hrsPerDayStartDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  hrsPerDayEndDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  hrsPerDayModel = {
    value: '00:00', isValid: true,validationMessage:''
  }

  hrsPerWeekStartDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  hrsPerWeekEndDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7);
  hrsPerWeekModel = {
    value: '01:00', isValid: true,validationMessage: ''
  }

  hrsPerWeekStep = 30;
  public monthsForm: FormGroup;
  initialFteDaysPerMonth = {
    JANUARY: '--',
    FEBRUARY: '--',
    MARCH: '--',
    APRIL: '--',
    MAY: '--',
    JUNE: '--',
    JULY: '--',
    AUGUST: '--',
    SEPTEMBER: '--',
    OCTOBER: '--',
    NOVEMBER: '--',
    DECEMBER: '--'
  };

  get monthMaxDays() {
    return {
      JANUARY: 31,
      FEBRUARY: 29,
      MARCH: 31,
      APRIL: 30,
      MAY: 31,
      JUNE: 30,
      JULY: 31,
      AUGUST: 31,
      SEPTEMBER: 30,
      OCTOBER: 31,
      NOVEMBER: 30,
      DECEMBER: 31
    }
  }
  monthType = {
    JANUARY: 'text',
    FEBRUARY: 'text',
    MARCH: 'text',
    APRIL: 'text',
    MAY: 'text',
    JUNE: 'text',
    JULY: 'text',
    AUGUST: 'text',
    SEPTEMBER: 'text',
    OCTOBER: 'text',
    NOVEMBER: 'text',
    DECEMBER: 'text'
  }
  get months(): string[] {
    return [
      'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
      'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'
    ];
  }

  constructor(private authService: AuthenticationService, private fb: FormBuilder, private translateService: TranslateService, private cdr: ChangeDetectorRef) {
    this.monthsForm = this.createMonthsFormGroup();
    effect(() => {
      this.setFteSettings(this.ctSettings());
    });
  }

  ngOnInit(): void {

  }

  createMonthsFormGroup(): FormGroup{
    let formControls = {

    }

    this.months.map(month => {
      formControls[month] = this.fb.control( '--',{validators:[
        Validators.required,
        Validators.pattern('^[0-9]*$'),
        Validators.max(this.monthMaxDays[month]),
        Validators.min(1)
      ],
      updateOn: 'change'
      });
    });

    return this.fb.group(formControls)
  }

  ngAfterViewInit(): void {
    // Prevent SOL's container click handler from refocusing input on bubbled clicks
    TextInputComponent.prototype.onInputContainerClick = function() {};

    this.initHrsPerDayPicker();
    this.initHrsPerWeekPicker();

    // Apply focus/blur fixes and input validation to both time pickers
    const pickerConfigs: Array<{ picker: TimePickerComponent, validate: (v: string) => void }> = [
      { picker: this.hrsPerDayPicker, validate: (v) => this.onHoursPerDayChange(v) },
      { picker: this.hrsPerWeekPicker, validate: (v) => this.onHoursPerWeekChange(v) },
    ];

    pickerConfigs.forEach(({ picker, validate }) => {
      (picker as any).validateAndUpdateTouched = () => {};
      (picker as any).onFocusOut = () => {};
      (picker as any).focusInput = () => {};

      // Override onChange to validate on every keystroke
      picker.onChange = (searchText: string) => {
        picker.selectedTimeString = searchText;
        validate(searchText);
      };

      // Override selectedTime to blur input after dropdown selection
      const origSelectedTime = picker.selectedTime.bind(picker);
      picker.selectedTime = (time: string) => {
        origSelectedTime(time);
        setTimeout(() => {
          const inputEl = picker.elementRef.nativeElement.querySelector('input') as HTMLInputElement;
          if (inputEl) {
            inputEl.blur();
          }
        }, 50);
      };

      // Gate focus() to only allow user-initiated focus
      setTimeout(() => {
        const inputEl = picker.elementRef.nativeElement.querySelector('input') as HTMLInputElement;
        const container = picker.elementRef.nativeElement.querySelector('.sol-input-container');

        if (inputEl) {
          let allowFocus = false;
          const origFocusFn = inputEl.focus.bind(inputEl);
          inputEl.focus = function() {
            if (allowFocus) {
              origFocusFn();
            }
          };
          inputEl.addEventListener('mousedown', () => { allowFocus = true; });
          inputEl.addEventListener('mouseup', () => { setTimeout(() => { allowFocus = false; }); });
          inputEl.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Tab') { allowFocus = true; setTimeout(() => { allowFocus = false; }); }
          });

          // Native input listener for deletions and manual typing
          inputEl.addEventListener('input', () => {
            const value = inputEl.value || '';
            picker.selectedTimeString = value;
            validate(value);
          });

          inputEl.addEventListener('focus', () => {
            container?.classList.add('esp-focused');
          });
          inputEl.addEventListener('blur', () => {
            container?.classList.remove('esp-focused');
          });
        }
      });
    });
  }

  initHrsPerDayPicker() {
    // Replace SOL's DatePipe-generated options with correct 24-hour format.
    // Range: 00:15–24:00 in 15-min increments.
    this.hrsPerDayPicker.timeOptions = [];
    for (let h = 0; h <= 23; h++) {
      for (let m = 0; m < 60; m += 15) {
        if (h === 0 && m === 0) continue; // skip 00:00
        this.hrsPerDayPicker.timeOptions.push(
          `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
        );
      }
    }
    this.hrsPerDayPicker.timeOptions.push("24:00");
  }

  initHrsPerWeekPicker() {
    // Replace SOL's DatePipe-generated options with correct 24-hour format.
    // Range: 01:00–168:00 in 30-min increments.
    this.hrsPerWeekPicker.timeOptions = [];
    for (let h = 1; h <= 168; h++) {
      this.hrsPerWeekPicker.timeOptions.push(
        `${String(h).padStart(2, '0')}:00`
      );
      if (h < 168) {
        this.hrsPerWeekPicker.timeOptions.push(
          `${String(h).padStart(2, '0')}:${this.hrsPerWeekStep}`
        );
      }
    }
    this.hrsPerWeekPicker.writeValue = ()=>{
      //must override the write value function to set a time string bigger than 24:00
    }
  }

  onHoursPerDayChange($event: any) {
    let value = this.hrsPerDayPicker.selectedTimeString;
    let {isValid,validationMessage} = this.getHoursPerDayValidation(value);
    if (isValid === true) {
      this.hrsPerDayModel.value = value;
    }else{
      this.hrsPerDayModel.validationMessage = validationMessage;
    }
    this.hrsPerDayModel.isValid = isValid;
    (this.hrsPerDayPicker as any).isValidTime = isValid;

    const container = this.hrsPerDayPicker.elementRef.nativeElement.querySelector('.sol-input-container');
    if (container) {
      isValid ? container.classList.remove('error-border') : container.classList.add('error-border');
    }

    this.notifyModelChange();
  }

  onHoursPerWeekChange($event: any) {
    let value = this.hrsPerWeekPicker.selectedTimeString;
    let {isValid,validationMessage} = this.getHoursPerWeekValidation(value);
    if (isValid === true) {
      this.hrsPerWeekModel.value = value;
    }else{
      this.hrsPerWeekModel.validationMessage = validationMessage;
    }
    this.hrsPerWeekModel.isValid = isValid;
    (this.hrsPerWeekPicker as any).isValidTime = isValid;

    const container = this.hrsPerWeekPicker.elementRef.nativeElement.querySelector('.sol-input-container');
    if (container) {
      isValid ? container.classList.remove('error-border') : container.classList.add('error-border');
    }

    this.notifyModelChange();
  }

  setFteSettings(ctSettings: { fteHoursPerDay: number | string; fteHoursPerWeek: number | string , fteDaysPerMonth: any}) {
    if(!ctSettings) return;

    if (ctSettings.fteHoursPerDay === "--:--") {
      this.hrsPerDayModel.value = "--:--";
    } else {
      this.hrsPerDayModel.value = DateUtils.convertMinutesToTimeString(ctSettings.fteHoursPerDay as number);
    }
    this.hrsPerDayModel.isValid = true;

    if(ctSettings.fteHoursPerWeek === "--:--") {
      this.hrsPerWeekModel.value = "--:--";
    } else {
      this.hrsPerWeekModel.value = DateUtils.convertMinutesToTimeString(ctSettings.fteHoursPerWeek as number);
    }
    this.hrsPerWeekModel.isValid = true;

    Object.keys(this.monthType).forEach((month)=>{
      this.monthType[month] = 'text';
      this.initialFteDaysPerMonth[month] = ctSettings.fteDaysPerMonth[month];
    });

    setTimeout(()=>{
      this.hrsPerDayPicker.selectedTimeString = this.hrsPerDayModel.value;
      this.hrsPerWeekPicker.selectedTimeString = this.hrsPerWeekModel.value;
      [this.hrsPerDayPicker, this.hrsPerWeekPicker].forEach((picker) => {
        const container = picker.elementRef.nativeElement.querySelector('.sol-input-container');
        container?.classList.remove('error-border');
      });
      (this.hrsPerDayPicker as any).cdr.detectChanges();
      (this.hrsPerWeekPicker as any).cdr.detectChanges();
      this.monthsForm.reset(this.initialFteDaysPerMonth);
    },0);
  }

  notifyModelChange() {
    let isValid = this.hrsPerDayModel.isValid && this.hrsPerWeekModel.isValid;

    let model = {
      fteHoursPerDay:  this.hrsPerDayModel.isValid && this.hrsPerDayModel.value!== "--:--" ? DateUtils.convertTimeStringToMinutes(this.hrsPerDayModel.value):undefined,
      fteHoursPerWeek: this.hrsPerWeekModel.isValid && this.hrsPerWeekModel.value!== "--:--" ?DateUtils.convertTimeStringToMinutes(this.hrsPerWeekModel.value):undefined,
      fteDaysPerMonth: {}
    };

    this.months.forEach((month) => {
      const days = this.monthsForm.get(month).value;
      model.fteDaysPerMonth[month] = this.monthsForm.get(month).valid && days !== "--" ? Number(days) : days;
      isValid = isValid && (this.monthsForm.get(month).valid || days == "--");
    });

    this.modelChanged.emit({
      fteHoursPerDay: model.fteHoursPerDay,
      fteHoursPerWeek: model.fteHoursPerWeek,
      fteDaysPerMonth: model.fteDaysPerMonth,
      isValid: isValid
    });
  }

  getHoursPerDayValidation(value: string) {
    const [hoursStr, minutesStr] = value.split(':');
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    // Check if hoursStr and minutesStr contain only numeric characters
    if (!/^\d+$/.test(hoursStr) || !/^\d+$/.test(minutesStr)) {
      return { isValid: false, validationMessage: 'plan.ct.settings.fte.hours.time.format.validation' };
    }

    if (hours < 0 || hours > 24) {
      return { isValid: false, validationMessage: 'plan.ct.settings.fte.hours.per.day.validation' };
    }

    // Check if both hours and minutes are present and are exactly two digits
    if (!hoursStr || !minutesStr || hoursStr.length !== 2 || minutesStr.length !== 2) {
      return { isValid: false, validationMessage: 'plan.ct.settings.fte.hours.time.format.validation' };
    }

    if (minutes < 0 || minutes >= 60) {
      return { isValid: false, validationMessage: 'plan.ct.settings.fte.hours.time.format.validation' };
    }

    if (hours === 0 && minutes < 15) {
      return { isValid: false, validationMessage: 'plan.ct.settings.fte.hours.per.day.validation' };
    }

    if (hours === 24 && minutes !== 0) {
      return { isValid: false, validationMessage: 'plan.ct.settings.fte.hours.per.day.validation' };
    }

    if (minutes % 5 !== 0) {
      return { isValid: false, validationMessage: 'plan.ct.settings.fte.hours.intervals5min.validation' };
    }

    return { isValid: true, validationMessage: '' };
  }

  getHoursPerWeekValidation(value: string) {
    const [hoursStr, minutesStr] = value.split(':');
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    // Check if hoursStr and minutesStr contain only numeric characters
    if (!/^\d+$/.test(hoursStr) || !/^\d+$/.test(minutesStr)) {
      return { isValid: false, validationMessage: 'plan.ct.settings.fte.hours.time.format.validation' };
    }

    if (hours < 1 || hours > 168) {
      return { isValid: false, validationMessage: 'plan.ct.settings.fte.hours.per.week.validation' };
    }

    if (!hoursStr || !minutesStr || hoursStr.length < 2 || hoursStr.length > 3 || minutesStr.length !== 2) {
      return { isValid: false, validationMessage: 'plan.ct.settings.fte.hours.time.format.validation' };
    }

    if (minutes < 0 || minutes >= 60) {
      return { isValid: false, validationMessage: 'plan.ct.settings.fte.hours.time.format.validation' };
    }

    if (hours === 168 && minutes !== 0) {
      return { isValid: false, validationMessage: 'plan.ct.settings.fte.hours.per.week.validation' };
    }

    if (minutes % 5 !== 0) {
      return { isValid: false, validationMessage: 'plan.ct.settings.fte.hours.intervals5min.validation' };
    }

    return { isValid: true, validationMessage: '' };
  }

  onMonthEdit(month: string) {
    this.monthType[month] = 'number';
    this.notifyModelChange();
  }

  getMonthName(number: number) {
    return this.translateService.instant("primeng.monthNames")[number];
  }
}
